import React, { useEffect, useRef, useState } from 'react';
import { brand } from '../../config/brand';
import SplitText from '../SplitText';
import HeroBot from '../HeroBot/HeroBot';
import './Hero.css';

const ROTATE_MS = 2600;

// Proof items resolve to their project for the link and the logo.
const proof = brand.liveProof.items.map((item) => {
  const project = brand.projects.find((p) => p.title === item.project);
  if (!project) throw new Error(`brand.liveProof: no project named ${item.project}`);
  return { ...item, project };
});

const Hero: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const words = brand.agency.heroWords;
  const [wordIndex, setWordIndex] = useState(0);

  // Auto-reveal hero content on mount (above the fold — no scroll needed)
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const items = section.querySelectorAll<HTMLElement>('.reveal');
    const timer = setTimeout(() => {
      items.forEach((el) => el.classList.add('is-visible'));
    }, 80);
    return () => clearTimeout(timer);
  }, []);

  // Cycle the headline keyword (respects reduced-motion)
  useEffect(() => {
    if (words.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = setInterval(() => setWordIndex((i) => (i + 1) % words.length), ROTATE_MS);
    return () => clearInterval(id);
  }, [words.length]);

  return (
    <section className="hero" id="hero" aria-label="Hero" ref={sectionRef}>
      <div className="hero__inner container">
        <div className="hero__copy">
          <p className="hero__avail reveal">
            <span className="hero__avail-dot" aria-hidden="true" />
            Available for new projects · {brand.agency.region}
          </p>

          <h1 className="hero__headline reveal reveal-delay-1">
            <SplitText text="We build AI-powered" />{' '}
            <span className="hero__word" aria-live="polite">
              {words.map((w, i) => (
                <span
                  key={w}
                  className={`hero__word-item pop-text${i === wordIndex ? ' hero__word-item--active' : ''}`}
                  aria-hidden={i !== wordIndex}
                >
                  {w}
                </span>
              ))}
              {/* Invisible sizer keeps the line width stable */}
              <span className="hero__word-sizer" aria-hidden="true">
                {words.reduce((a, b) => (a.length >= b.length ? a : b), '')}
              </span>
            </span>
            <br />
            <SplitText text="that scale." />
          </h1>

          <p className="hero__sub reveal reveal-delay-2">{brand.agency.description}</p>

          <div className="hero__ctas reveal reveal-delay-3">
            <a href="#contact" className="btn btn-primary" id="hero-cta-primary">
              {brand.agency.ctaPrimary}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
            </a>
            <a href="#projects" className="btn btn-outline" id="hero-cta-secondary">
              {brand.agency.ctaSecondary}
            </a>
          </div>

          <div className="hero__proof reveal reveal-delay-4">
            <p className="hero__proof-label" id="hero-proof-label">
              <span className="hero__avail-dot" aria-hidden="true" />
              {brand.liveProof.label}
            </p>
            <ul className="hero__proof-list" aria-labelledby="hero-proof-label">
              {proof.map(({ project, what, logoHeight }) => (
                <li key={project.title}>
                  <a
                    className="hero__proof-item"
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    id={`hero-proof-${project.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <img className="hero__proof-logo" src={project.logo} alt={project.title} style={{ height: logoHeight }} decoding="async" />
                    <span className="hero__proof-what">
                      {what}
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M7 17 17 7M8 7h9v9" /></svg>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="hero__aside reveal reveal-delay-2">
          <HeroBot />
        </div>
      </div>

      <div className="hero__clients container reveal reveal-delay-5">
        <span className="hero__clients-label">{brand.clientsLabel}</span>
        <ul className="hero__clients-list" aria-label="Clients">
          {brand.clients.map((c) => (
            <li key={c.name}>
              <img
                className="hero__client-logo"
                src={c.logo}
                alt={c.name}
                style={{ height: c.logoHeight }}
                loading="lazy"
                decoding="async"
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default Hero;
